import * as React from "react";
import { useParams } from "react-router-dom";
import { Accept, useDropzone } from "react-dropzone";
import useDrivePicker from "react-google-drive-picker";
import cn from "classnames";
import { Button, Card, Input, Select } from "@jhool-io/fe-components";
import { useFetchClientBasicAndPersonalInfo } from "../../hooks/queries/client";
import useFileUpload from "../../hooks/useFileUpload/useFileUpload";
import useToast from "../../hooks/useToast";
import {
    ClientDocumentTypes,
    DocumentUploadType,
} from "../../utils/types/client";
import {
    IThirdPartyDocument,
    SupportedUploadMethod,
    ThirdPartyType,
} from "../../utils/types/filepicker";
import {
    makeStringFirstLetterCapital,
    removeEnumUnderscore,
} from "../../utils/helpers";
import FolderOpenIcon from "../Icons/FolderOpen";
import LinkIcon from "../Icons/Link";
import PlusIcon from "../../icons/Plus";
import TimesIcon from "../../icons/Times";

interface FilePickerProps extends React.InputHTMLAttributes<HTMLInputElement> {
    /** Allow multiple uploads */
    allowMultiple?: boolean;
    /** Detemines upload methods to show */
    uploadMethods: {
        method: SupportedUploadMethod;
    }[];
    /** Type of file to be uploaded */
    uploadType: DocumentUploadType;
    /** Insurance id if uploading insurance document */
    insuranceId?: string;
    /** Callback to call after successful upload */
    successCallback: () => void;
    /** A default document type value  */
    documentToUploadType?: ClientDocumentTypes;
    /** RemitId id if uploading remit eob */
    remitId?: string;
    /** claimId id if uploading claims attachment */
    claimId?: string;
    /** Name on remit file */
    remitName?: string;
    /** Text for the submit action * */
    actionText?: string;
    /** Show cancel button * */
    showCancelButton?: boolean;
    /** callback for when the user clicks on cancel button * */
    cancelBtnClick?: () => void;
    /** Document types accepted text */
    acceptedTypes?: string;
    /** dropzone accepted file types */
    dropzoneAcceptedTypes?: Accept;
    /** Google drive accepted file types
     * check https://www.npmjs.com/package/react-google-drive-picker viewID options
     * for allowed values
     */
    googleDriveViewIdOption?:
        | "DOCS"
        | "DOCS_IMAGES"
        | "DOCS_IMAGES_AND_VIDEOS"
        | "DOCS_VIDEOS"
        | "DOCUMENTS"
        | "DRAWINGS"
        | "FOLDERS"
        | "FORMS"
        | "PDFS"
        | "SPREADSHEETS"
        | "PRESENTATIONS";
}

// Select options type
type SelectOption = {
    label: string;
    value: ClientDocumentTypes;
};

//  Select options for document type select input
const selectOptionsForDocumentType: SelectOption[] = [
    ...Object.values(ClientDocumentTypes).map((item) => ({
        value: item,
        label: makeStringFirstLetterCapital(removeEnumUnderscore(item)),
    })),
];

const maxFileSizeInBytes = 10 * 1024 * 1024;

export default function FilePicker({
    allowMultiple = true,
    uploadMethods,
    uploadType,
    insuranceId,
    successCallback,
    documentToUploadType,
    remitId,
    claimId,
    remitName,
    actionText = "Upload",
    showCancelButton,
    cancelBtnClick,
    acceptedTypes,
    dropzoneAcceptedTypes,
    googleDriveViewIdOption,
    ...props
}: FilePickerProps) {
    // Local component states
    const [documentType, setDocumentType] =
        React.useState<ClientDocumentTypes | null>(
            documentToUploadType || null
        );
    const [selectedFiles, setSelectedFiles] = React.useState<File[] | null>(
        null
    );
    const [googleDocFiles, setGoogleDocFiles] = React.useState<
        IThirdPartyDocument[] | null
    >(null);
    const [fileName, setFileName] = React.useState<string | null>(null);
    const shouldShowFileNameInput = !["claims-file", "documents"].includes(
        uploadType
    );

    // Initialize the google drive picker hook
    const [openPicker] = useDrivePicker();

    const { toast } = useToast();

    // Function to call when google drive option is clicked
    const handleOpenGooglePicker = () => {
        openPicker({
            clientId: import.meta.env.VITE_GOOGLE_CLIENT_ID,
            developerKey: import.meta.env.VITE_GOOGLE_DEVELOPER_KEY,
            viewId: googleDriveViewIdOption || "DOCS",
            showUploadView: true,
            showUploadFolders: true,
            supportDrives: true,
            multiselect: true,
            callbackFunction: (data) => {
                // Do not perform any action if user closes the popup
                if (data.action === "cancel") return;
                // Clear selected files from normal input element
                if (
                    data?.docs &&
                    data.docs.length > 0 &&
                    selectedFiles &&
                    selectedFiles.length > 0
                ) {
                    setSelectedFiles(null);
                }
                /** Check if list is empty or not and decide whether to
                   append new files or not
                */
                const formatData: IThirdPartyDocument[] = data.docs.map(
                    (file) => ({
                        document_url: file.url,
                        third_party_id: file.id,
                        document_name: file.name,
                        third_party_type: ThirdPartyType.GOOGLE_DRIVE,
                    })
                );
                if (googleDocFiles && googleDocFiles.length > 0) {
                    setGoogleDocFiles([...formatData, ...googleDocFiles]);
                } else {
                    setGoogleDocFiles([...formatData]);
                }
            },
        });
    };

    const handleAddSelectedFiles = (files: File[]) => {
        const emptyFileNames = files
            .filter((file) => file.size === 0)
            .map((file) => file.name)
            .join(", ");
        const oversizedFileNames = files
            .filter((file) => file.size > maxFileSizeInBytes)
            .map((file) => file.name)
            .join(", ");
        const uploadableFiles = files.filter(
            (file) => file.size > 0 && file.size <= maxFileSizeInBytes
        );

        if (emptyFileNames) {
            toast({
                mode: "error",
                message: `Could not attach ${emptyFileNames}. Empty files cannot be uploaded. Please save a copy to your computer and try again.`,
            });
        }
        if (oversizedFileNames) {
            toast({
                mode: "error",
                message: `Could not attach ${oversizedFileNames}. Maximum file size is 10 MB.`,
            });
        }
        if (uploadableFiles.length === 0) return false;
        if (selectedFiles && selectedFiles.length > 0) {
            setSelectedFiles([...uploadableFiles, ...selectedFiles]);
        } else setSelectedFiles([...uploadableFiles]);
        return true;
    };

    // Initialize the useDropzone hook
    const { getRootProps, isDragActive } = useDropzone({
        onDrop: handleAddSelectedFiles,
        maxFiles: uploadType === "insurance-card" ? 2 : 0,
        accept: dropzoneAcceptedTypes || {
            "application/pdf": [".pdf"],
            "image/*": [".png", ".webp", ".jpeg", ".jpg", ".avif", ".svg"],
        },
    });

    // Get id from url params
    const params = useParams();
    const clientId = params.clientId as string;

    // Hook for fetching client basic and personal info
    const { data } = useFetchClientBasicAndPersonalInfo(clientId);

    // Initialize the file upload hook
    const {
        mutation,
        uploadProgress,
        thirdPartyMutation,
        remitFileMutation,
        claimsMutation,
    } = useFileUpload({
        uploadType,
        files: selectedFiles as File[],
        client: data?.data,
        clientId,
        type: documentType || undefined,
        insuranceId: insuranceId || "",
        successCallback,
        documents: googleDocFiles || [],
        remitId,
        claimId,
        remitName,
        fileName: fileName || "",
    });

    // Function to determine the upload methods to show
    const handleCheckIfUploadMethodIsPresent = (
        method: SupportedUploadMethod
    ) => {
        const methodIndex = uploadMethods.findIndex(
            (item) => item.method === method
        );

        return methodIndex === -1 ? null : uploadMethods[methodIndex];
    };

    // Function to remove a selected file
    const handleRemoveSelectedFile = (filename: string) => {
        if (selectedFiles) {
            const filesArr = [...selectedFiles].filter(
                (file) => file.name !== filename
            );
            if (filesArr.length === 0) setSelectedFiles(null);
            else setSelectedFiles(filesArr);
        }
    };

    // Function for removing google third party selected file
    const handleRemoveGooglePickerFile = (filename: string) => {
        if (googleDocFiles) {
            const filesArr = googleDocFiles.filter(
                (file) => file.document_name !== filename
            );
            if (googleDocFiles.length === 0) setGoogleDocFiles(null);
            else setGoogleDocFiles(filesArr);
        }
    };

    // Function for setting selected files
    const handleSetSelectedFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            const hasAddedFiles = handleAddSelectedFiles([...e.target.files]);
            // Clear selected files from third party picker

            if (googleDocFiles && googleDocFiles.length > 0 && hasAddedFiles) {
                setGoogleDocFiles(null);
            }
        }
    };

    // Function for uploading document(s)
    const handleSaveButtonClicked = () => {
        mutation.mutate();
    };

    // Function for uploading document(s) from third party
    const handleSubmitThirdPartyUpload = () => {
        thirdPartyMutation.mutate();
    };

    const handleRemitFileUpload = () => {
        remitFileMutation.mutate();
    };

    const handleClaimsFileUpload = () => {
        claimsMutation.mutate();
    };

    const handleUpload = () => {
        if (remitId) {
            handleRemitFileUpload();
        } else if (claimId) {
            handleClaimsFileUpload();
        } else if (googleDocFiles) {
            handleSubmitThirdPartyUpload();
        } else {
            handleSaveButtonClicked();
        }
    };

    return (
        <div className="relative">
            <div className="pb-[60px] overflow-y-auto h-auto px-4">
                <p className="text-base font-semibold mb-3">
                    Add new documents
                </p>
                <p className="text-caption font-normal mb-1">
                    You can upload multiple files at the same time
                </p>
                {handleCheckIfUploadMethodIsPresent(
                    SupportedUploadMethod.manualupload
                ) && (
                    <div className="flex flex-col gap-y-4 mt-4">
                        {shouldShowFileNameInput && (
                            <Input
                                label="File Name"
                                placeholder="Enter file name"
                                onChange={(event) =>
                                    setFileName(event.target.value)
                                }
                            />
                        )}
                        {uploadType === "documents" && (
                            <Select
                                placeholder="Select document type"
                                label="Document type"
                                options={selectOptionsForDocumentType}
                                onChange={(value) =>
                                    setDocumentType(
                                        (value as SelectOption).value
                                    )
                                }
                                defaultValue={documentType}
                                isLongListInDialog
                            />
                        )}
                        <div
                            className={cn(
                                "flex w-full items-center gap-x-[--spacing-items]  border border-dashed border-[--color-text-link] rounded-lg px-[--padding-card-x] py-[--padding-card-y]",
                                {
                                    "border-[2px] border-dashed border-stroke":
                                        isDragActive,
                                }
                            )}
                            {...getRootProps()}
                        >
                            <div className="p-16  bg-[rgba(204,250,233,0.50)] rounded-md shrink-0">
                                <FolderOpenIcon />
                            </div>
                            <div className="flex flex-col">
                                <div className="text-base font-medium">
                                    <label
                                        htmlFor="file-upload"
                                        className="font-semibold text-textLink"
                                    >
                                        Click to upload{" "}
                                        <input
                                            className="w-full absolute left-[-99999px]"
                                            id="file-upload"
                                            name="file-upload"
                                            type="file"
                                            multiple={allowMultiple}
                                            onChange={handleSetSelectedFiles}
                                            max="2"
                                            {...props}
                                        />
                                    </label>
                                    or drag your file here
                                </div>
                                <p className="text-caption font-normal">
                                    {`Maximum file size of 10 MB: ${
                                        acceptedTypes ||
                                        ".png, .jpg, .jpeg, .svg"
                                    }`}
                                </p>
                            </div>
                        </div>

                        {uploadProgress > 0 && (
                            <div className="mt-[--spacing-items] h-2 bg-stroke rounded-md">
                                <div
                                    role="progressbar"
                                    aria-label="file upload progress value"
                                    aria-valuenow={uploadProgress}
                                    style={{ width: `${uploadProgress}%` }}
                                    className="h-full rounded-r8 bg-primary rounded-md"
                                />
                            </div>
                        )}
                    </div>
                )}
                {(handleCheckIfUploadMethodIsPresent(
                    SupportedUploadMethod.googledrive
                ) ||
                    handleCheckIfUploadMethodIsPresent(
                        SupportedUploadMethod.dropbox
                    )) && (
                    <div>
                        {handleCheckIfUploadMethodIsPresent(
                            SupportedUploadMethod.googledrive
                        ) && (
                            <Button
                                variant="normal"
                                aria-label="upload with googledrive"
                                onClick={handleOpenGooglePicker}
                                className="text-base font-medium mt-[--spacing-items] p-0 gap-x-2"
                            >
                                <PlusIcon />
                                Or upload from google drive
                            </Button>
                        )}
                    </div>
                )}

                <div>
                    <div className="flex items-center gap-x-2 text-caption font-semibold">
                        <LinkIcon />
                        {selectedFiles?.length ||
                            googleDocFiles?.length ||
                            0}{" "}
                        files added
                    </div>
                    {googleDocFiles &&
                        googleDocFiles.map((file, i) => (
                            <div
                                key={`${file.document_name} - ${i + 1}`}
                                className="border-b border-strokedark last:border-b-0"
                            >
                                <div className="flex items-center justify-between w-full [border:1px_solid_var(--color-stroke)] border border-strokedark rounded-md px-[--padding-card-x] py-[--padding-card-y] my-[--spacing-items]">
                                    <div className="flex flex-col">
                                        <span className="text-caption font-medium">
                                            {`Client - ${
                                                documentType
                                                    ? documentType.replaceAll(
                                                          "-",
                                                          " "
                                                      )
                                                    : "document"
                                            }`}
                                        </span>
                                        <span className="font-normal text-caption text-textSec">
                                            {file.document_name}
                                        </span>
                                    </div>
                                    <Button
                                        variant="can-hover"
                                        aria-label="delete icon"
                                        onClick={() =>
                                            handleRemoveGooglePickerFile(
                                                file.document_name
                                            )
                                        }
                                    >
                                        <TimesIcon />
                                    </Button>
                                </div>
                            </div>
                        ))}
                    {selectedFiles &&
                        selectedFiles.map((file, i) => (
                            <div
                                key={`${file.name} - ${i + 1}`}
                                className="border-b border-strokedark last:border-b-0"
                            >
                                <div className="flex items-center justify-between w-full [border:1px_solid_var(--color-stroke)] border border-strokedark rounded-md px-[--padding-card-x] py-[--padding-card-y] my-[--spacing-items]">
                                    <div className="flex flex-col">
                                        <span className="text-caption font-medium">
                                            {`Client - ${
                                                documentType
                                                    ? removeEnumUnderscore(
                                                          documentType
                                                      )
                                                    : "document"
                                            }`}
                                        </span>
                                        <span className="text-caption text-xs text-textSec">
                                            {file.name}
                                        </span>
                                    </div>
                                    <Button
                                        variant="can-hover"
                                        aria-label="delete icon"
                                        onClick={() =>
                                            handleRemoveSelectedFile(file.name)
                                        }
                                    >
                                        <TimesIcon />
                                    </Button>
                                </div>
                            </div>
                        ))}
                </div>
            </div>

            <Card classNames="fixed bottom-0 left-0 flex [border:none] justify-end  ![border-top:1px_solid_var(--color-stroke)] !rounded-none w-full gap-[--spacing-items]">
                {showCancelButton && (
                    <Button variant="secondary" onClick={cancelBtnClick}>
                        Cancel
                    </Button>
                )}
                <Button
                    disabled={
                        uploadType === "insurance-file"
                            ? (!selectedFiles && !googleDocFiles) ||
                              mutation.isLoading ||
                              thirdPartyMutation.isLoading
                            : (!selectedFiles && !googleDocFiles) ||
                              !documentType ||
                              mutation.isLoading ||
                              thirdPartyMutation.isLoading ||
                              claimsMutation.isLoading
                    }
                    onClick={handleUpload}
                >
                    {actionText}
                </Button>
            </Card>
        </div>
    );
}
